import React, { useEffect, useState, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  FileText,
  Upload,
  Trash2,
  ExternalLink,
  FolderOpen,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { studentAPI } from '../../services/api';
import { useInteractionMode } from '../../hooks/useInteractionMode';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const DOCUMENT_TYPE_LABELS = {
  PASSPORT:            'Passport',
  TRANSCRIPT:          'Academic Transcript',
  IELTS_SCORE:         'IELTS Score Report',
  TOEFL_SCORE:         'TOEFL Score Report',
  RESUME:              'Resume / CV',
  SOP:                 'Statement of Purpose',
  LOR:                 'Letter of Recommendation',
  FINANCIAL_STATEMENT: 'Financial Statement',
  PHOTO:               'Photograph',
  OTHER:               'Other Document',
};

const DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_LABELS);

const STATUS_DISPLAY = {
  APPROVED: { label: 'Approved',  className: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle },
  REJECTED: { label: 'Rejected',  className: 'bg-red-100 text-red-700',         Icon: XCircle },
  PENDING:  { label: 'Pending',   className: 'bg-amber-100 text-amber-700',     Icon: Clock },
};

export default function DocumentsPage() {
  const { isRepManaged } = useInteractionMode();
  const [documents, setDocuments]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [selectedType, setSelectedType] = useState('OTHER');
  const fileRef = useRef(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await studentAPI.getDocuments();
      setDocuments(res.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async e => {
    if (isRepManaged) {
      toast.error('Your account is managed by a representative. They handle document uploads.');
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await studentAPI.uploadDocument(file, selectedType);
      toast.success('Document uploaded successfully');
      fetchDocuments();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async documentType => {
    if (isRepManaged) {
      toast.error('Your account is managed by a representative. They handle document management.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await studentAPI.deleteDocument(documentType);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.documentType !== documentType));
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const approvedCount = documents.filter(d => d.status === 'APPROVED').length;
  const pendingCount  = documents.filter(d => !d.status || d.status === 'PENDING').length;

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Documents</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage all your uploaded documents in one place
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:border-[#1C8DEE] focus:ring-1 focus:ring-[#1C8DEE]/30 transition"
            >
              {DOCUMENT_TYPES.map(t => (
                <option key={t} value={t}>{DOCUMENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
            <Button
              className="bg-[#1C8DEE] hover:bg-[#1a7fd4] text-white rounded-lg shadow-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
            {/* Hidden file input */}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
            <p className="text-sm text-gray-500 mt-0.5">Total Uploaded</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Approved</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Pending Review</p>
          </div>
        </div>

        {/* Document list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
            ))}
          </div>
        ) : documents.length > 0 ? (
          <Card className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-50">
              {documents.map((doc, i) => {
                const statusKey = (doc.status || 'PENDING').toUpperCase();
                const sDsp = STATUS_DISPLAY[statusKey] || STATUS_DISPLAY.PENDING;
                const StatusIcon = sDsp.Icon;

                return (
                  <div
                    key={doc.documentType || i}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* File icon */}
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#1C8DEE]" />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType || 'Document'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {doc.uploadedAt
                          ? `Uploaded ${format(new Date(doc.uploadedAt), 'MMM d, yyyy')}`
                          : doc.createdAt
                          ? `Added ${formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true })}`
                          : 'Recently uploaded'}
                      </p>
                    </div>

                    {/* Status badge */}
                    <span className={`hidden sm:flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${sDsp.className}`}>
                      <StatusIcon className="w-3 h-3" />
                      {sDsp.label}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-[#1C8DEE] hover:bg-blue-50 transition-colors"
                          title="Open document"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(doc.documentType)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete document"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ) : (
          /* Empty state */
          <div className="text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No documents yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">
              Upload your passport, transcripts, test scores, and other required documents
            </p>
            <Button
              className="bg-[#1C8DEE] hover:bg-[#1a7fd4] text-white rounded-lg"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-1.5" />
              Upload your first document
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
